import styled from 'styled-components';

// --- General Layout and Reset ---
export const PageContainer = styled.div`
  font-family: Arial, sans-serif; /* Adjusted to a common web font */
  color: #333;
  line-height: 1.6;
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
`;

export const Section = styled.section`
  padding: 40px 10%;
  margin-bottom: 20px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 30px 5%;
  }
`;

export const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

export const TextContent = styled.div`
  flex: 1;
  min-width: 300px;
`;

export const ImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 300px;
`;

export const Image = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
`;

export const Heading = styled.h3`
  font-size: 1.5em;
  color: #000; /* Assuming black for general headings */
  margin-bottom: 10px;
`;

export const Paragraph = styled.p`
  font-size: 1em;
  margin-bottom: 15px;
  color: #555;
`;

// --- Header/Hero Section Styles (The top yellow/white section) ---
export const HeaderSection = styled(Section)`
  background: white; /* Match the top part of the image */
  padding-top: 80px;
  padding-bottom: 80px;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

export const TitleContainer = styled.div`
  flex: 1;
  min-width: 300px;
`;

export const MainTitle = styled.h1`
  font-size: 3.5em;
  font-weight: 900;
  color: #555; /* Adjusted to a darker color for readability */
  line-height: 1.1;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 2.5em;
  }
`;

export const SubTitle = styled.h2`
  font-size: 2em;
  font-weight: 300;
  color: #555;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 1.5em;
  }
`;

export const ContactButton = styled.button`
  background-color: #f7a83b; /* Orange color from the design */
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #e5972a;
  }
`;

export const HeaderImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  min-width: 300px;

  /* This is a placeholder for the complex diagram image */
  &::before {
    content: 'HEADER DIAGRAM IMAGE';
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 250px; /* Placeholder height */
    background-color: #f0f0f0;
    border: 1px dashed #ccc;
    color: #999;
    font-size: 1.2em;
  }

  @media (max-width: 900px) {
    justify-content: center;
    margin-top: 30px;
  }
`;


// --- Key Aspects Section Styles (The dark grey section) ---
export const KeyAspectsSection = styled(Section)`
  background-color: #4a4a4a; /* Dark grey from the design */
  color: white;
  text-align: center;
  padding: 60px 10%;
`;

export const KeyAspectsTitle = styled.h2`
  font-size: 2em;
  color: #f7a83b; /* Orange text for emphasis */
  margin-bottom: 10px;
  padding: 15px 30px;
  background: linear-gradient(to right, #4a4a4a 0%, #f7a83b 50%, #4a4a4a 100%);
  border-radius: 5px;
  display: inline-block;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
`;

export const KeyAspectsSubtitle = styled.p`
  font-size: 1em;
  color: #ccc;
  margin-bottom: 40px;
`;

// --- Feature/Content Grid Styles (The white sections below key aspects) ---
export const FeatureSection = styled(Section)`
  background: white;
  padding-top: 40px;
`;

export const FeatureBox = styled.div`
  flex: 1;
  min-width: 45%;
  padding: 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-width: 100%;
    padding: 10px 0;
  }
`;

export const FeatureTitle = styled.h4`
  font-size: 1.3em;
  font-weight: bold;
  color: #f7a83b; /* Orange for feature titles */
  margin-bottom: 15px;
  text-transform: uppercase;
`;

// Specific layout styles for the different content blocks

export const EnhancedEfficiencyBlock = styled(FlexContainer)`
  flex-direction: row-reverse; /* Image on the left, text on the right */

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const ModularStructureBlock = styled(FlexContainer)`
  flex-direction: row; /* Image on the right, text on the left */
  margin-top: 40px;
`;

export const StagesBlock = styled(FlexContainer)`
  flex-direction: row-reverse; /* Image on the left, text on the right */
  margin-top: 40px;
`;

export const DynamicStylesBlock = styled(FlexContainer)`
  flex-direction: row; /* Image on the right, text on the left */
  margin-top: 40px;
`;

